import Vue from 'vue';
import Router from 'vue-router';

import {
  commonLanguageLogic,
  setLanguage,
  updateLanguageMessages,
} from '@motionelements/core/src/services/language.service.js';
import { setLocale as setLinkLocale } from '@motionelements/core/src/helpers/link.js';
import { getPartnerEmbedConfig } from '@/config/partnerEmbed.js';
import i18n from '@/lang/i18n.js';
import { preLoadedLanguages } from '@/lang';
import routes from './routes';

const validLanguageCodes = process.env.VUE_APP_LANGUAGE_CODES.split(',');

function isEmbedRoute(to) {
  if (to.meta.isPartnerEmbed || to.meta.isProductEmbed || to.meta.isLightboxEmbed) {
    return true;
  }

  if (to.path.startsWith('/embed/')) {
    return true;
  }

  return to.params.lang
    && validLanguageCodes.includes(to.params.lang)
    && to.path.startsWith(`/${to.params.lang}/embed/`);
}

function resolveEmbedLanguageCode(to) {
  if (to.params.lang && validLanguageCodes.includes(to.params.lang)) {
    return to.params.lang;
  }

  if (to.meta.isPartnerEmbed) {
    const partnerConfig = getPartnerEmbedConfig(to.params.partner);
    if (partnerConfig) {
      return partnerConfig.locale;
    }
  }

  return 'en';
}

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: routes,
  scrollBehavior(to, from) {
    if (to.path === from.path) {
      return false;
    }

    const position = { x: 0, y: 0 };
    // eslint-disable-next-line
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(position);
      }, 0);
    });
  },
});

function loadLanguageAndContinue(to, from, next, languageCode) {
  setLanguage(languageCode, { to, from }, i18n);
  setLinkLocale(languageCode);

  if (preLoadedLanguages.includes(languageCode)) {
    next();
    return;
  }

  import(/* webpackChunkName: "i18n-[request]" */ `@/lang/json/${languageCode}.json`)
    .then(messages => {
      updateLanguageMessages({ to, from, next }, languageCode, messages, preLoadedLanguages, i18n);
      next();
    })
    .catch(() => {
      next();
    });
}

router.beforeEach((to, from, next) => {
  if (isEmbedRoute(to)) {
    if (to.meta.isPartnerEmbed && !getPartnerEmbedConfig(to.params.partner)) {
      next();
      return;
    }

    loadLanguageAndContinue(to, from, next, resolveEmbedLanguageCode(to));
    return;
  }

  commonLanguageLogic({ to, from }, preLoadedLanguages, i18n)
    .then(languageCode => {
      if (languageCode) { // lazy load language
        import(/* webpackChunkName: "i18n-[request]" */ `@/lang/json/${languageCode}.json`)
          .then(messages => {
            updateLanguageMessages({ to, from, next }, languageCode, messages, preLoadedLanguages, i18n);
            next();
          })
          .catch(() => {
            next();
          });
      } else {
        next();
      }
    });
});

export default router;
